import React, { useEffect, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import _ from "lodash"

import Api from "@/api"
import AsyncSearch, { DataStates } from "@/helpers/AsyncSearch"
import LoadingSpinner from "@/components/LoadingSpinner"
import { newAxiosErrorHandler } from "@/osn"
import { deepSnakeizeKeys, isBlank } from "@/utils"
import {
  getFieldUpdateContext,
  getFieldValues,
  getProductId,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import { useProductSubmitting } from "@/onboarding/features/product/helpers"

import SectionDiffTable from "./SectionDiffTable"
import { getImportSections, ImportSection } from "./importSections"

interface SectionDiff extends ImportSection {
  diff: Record<string, any>
}

const ImportTourModal = ({ show, onHide }: { show: boolean; onHide: () => void }): JSX.Element => {
  const dispatch = useDispatch()
  const productId = useSelector(getProductId)
  const context = useSelector(getFieldUpdateContext)
  const sections = useSelector(getImportSections)

  const allFieldNames = _.uniq(sections.flatMap((section) => section.fieldNames))
  const currentValues = useSelector(getFieldValues(...allFieldNames))

  const [dataState, setDataState] = useState(DataStates.LOADING)
  const [searchMeta, setSearchMeta] = useState(null)
  const [source, setSource] = useState(null)
  const [diffValues, setDiffValues] = useState(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setSubmitting] = useProductSubmitting()

  const diffLoaded = !isBlank(source) && !_.isNull(diffValues)

  // In bid context the source list spans every tour where the operator holds
  // a bid; product (cruise line) context stays shared-code.
  const bidContext = !isBlank(context.bidId)
  const truncated = searchMeta && searchMeta.totalCount > searchMeta.shown

  const sectionDiffs: SectionDiff[] = diffLoaded
    ? sections
        .map((section) => ({ ...section, diff: _.pick(diffValues, section.fieldNames) }))
        .filter((section) => !_.isEmpty(section.diff))
    : []

  useEffect(() => {
    setDiffValues(null)
    setSelected({})
    setExpanded({})

    if (isBlank(source)) {
      return
    }

    Api.onboardingProduct
      .fieldImportDiff(productId, allFieldNames, context, source.value)
      .then(({ data }) => setDiffValues(deepSnakeizeKeys(data.data.diffValues)))
      .catch(newAxiosErrorHandler(dispatch))
  }, [source])

  // A section is "new content" when every differing field is blank on the
  // current tour — importing fills gaps rather than replacing values.
  const newContent = (section: SectionDiff) =>
    _.every(_.keys(section.diff), (fieldName) => isBlank(currentValues[fieldName]))

  // Default selection: new-content sections import by default; sections
  // holding deliberately different values must be opted into explicitly.
  useEffect(() => {
    if (_.isNull(diffValues)) {
      return
    }

    setSelected(_.fromPairs(sectionDiffs.map((section) => [section.name, newContent(section)])))
  }, [diffValues])

  // Return to the page once the import round-trip settles.
  useEffect(() => {
    if (submitted && !isSubmitting) {
      onHide()
    }
  }, [submitted, isSubmitting])

  const allSelected = !_.isEmpty(sectionDiffs) && sectionDiffs.every((s) => selected[s.name])
  const anySelected = sectionDiffs.some((s) => selected[s.name])

  const toggleSection = (name: string) => setSelected({ ...selected, [name]: !selected[name] })
  const toggleExpanded = (name: string) => setExpanded({ ...expanded, [name]: !expanded[name] })
  const toggleAll = () =>
    setSelected(_.fromPairs(sectionDiffs.map((section) => [section.name, !allSelected])))

  const importSelected = () => {
    const values = sectionDiffs.reduce(
      (acc, section) => (selected[section.name] ? { ...acc, ...section.diff } : acc),
      {}
    )

    if (_.isEmpty(values)) {
      return
    }

    setSubmitted(true)
    setSubmitting(true)
    dispatch(updateFields({ values }))
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="import-tour-modal-title"
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title id="import-tour-modal-title">Import from Tour</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <Form.Group>
          <Form.Label>
            Source Tour
            <i
              title="Search tours for importable values and choose the sections to import. Importing replaces this tour's values for the selected sections."
              className="fas fa-info-circle d-inline ms-2 cursor-pointer"
            />
          </Form.Label>
          <AsyncSearch
            selectedValues={_([source]).compact().value()}
            setSelectedValues={(results) => setSource(_.head(results) || null)}
            getOptions={(query) =>
              // The whole-tour import diffs every section, component lists
              // included, so the search must know the full field set — without
              // it the picker offers split-appointment sources that
              // require_importable_component_fields! then rejects.
              Api.onboardingProduct.searchImportableProducts(context, query, allFieldNames)
            }
            onOptionsLoaded={(state, meta) => {
              setDataState(state)
              setSearchMeta(meta)
            }}
            dataState={dataState}
            absentDataMessage={
              bidContext ? "You hold no bids on other tours." : "No tours share this tour code."
            }
            maxSelectable={1}
          />
          <Form.Text muted>
            {bidContext
              ? truncated
                ? `Showing ${searchMeta.shown} of ${searchMeta.totalCount} tours — typing a tour name, code or port searches all ${searchMeta.totalCount}.`
                : "Tours where you hold a bid — this tour's code and port rank first."
              : "Tour ideas and tours sharing this tour code."}
          </Form.Text>
        </Form.Group>

        {!isBlank(source) && !diffLoaded && (
          <div className="d-flex align-items-center gap-2">
            Comparing tour values...
            <LoadingSpinner />
          </div>
        )}

        {diffLoaded && _.isEmpty(sectionDiffs) && (
          <p className="mb-0">No difference in importable values. Please select another tour.</p>
        )}

        {!_.isEmpty(sectionDiffs) && (
          <>
            <Form.Check
              type="checkbox"
              id="import-section-select-all"
              checked={allSelected}
              onChange={toggleAll}
              label={<strong>Select all sections</strong>}
            />
            <div className="d-flex flex-column gap-2">
              {sectionDiffs.map((section) => (
                <div key={section.name} className="border rounded p-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <Form.Check
                      type="checkbox"
                      id={`import-section-${section.name}`}
                      checked={!!selected[section.name]}
                      onChange={() => toggleSection(section.name)}
                      label={
                        <span>
                          <strong>{section.title}</strong>
                          <span className="ms-2 text-muted">
                            {_.size(section.diff)} field(s) differ —{" "}
                            {newContent(section)
                              ? "new content here, selected for import"
                              : "would replace values already entered here"}
                          </span>
                        </span>
                      }
                    />
                    <Button variant="link" size="sm" onClick={() => toggleExpanded(section.name)}>
                      {expanded[section.name] ? "Hide values" : "Show values"}
                    </Button>
                  </div>
                  {expanded[section.name] && (
                    <SectionDiffTable diff={section.diff} currentValues={currentValues} />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-start">
        <Button size="sm" onClick={importSelected} disabled={!anySelected || isSubmitting}>
          <i className="fas fa-save me-2" />
          Import and Replace
        </Button>
        <Button size="sm" variant="outline-danger" onClick={onHide} disabled={isSubmitting}>
          <i className="fas fa-times me-2" />
          Cancel
        </Button>
        {isSubmitting && <LoadingSpinner />}
      </Modal.Footer>
    </Modal>
  )
}

export default ImportTourModal
